import React, { useState, useRef } from 'react';
import { Paper, Text, Button, Group, Progress, Alert, Stack } from '@mantine/core';
import { IconUpload, IconMusic, IconCheck, IconX } from '@tabler/icons-react';
import { supabase } from '../../lib/supabase';

/**
 * MusicUpload Component
 * Handles music file upload with validation:
 * - File format: mp3, wav, aac, m4a, flac, ogg
 * - Duration: at most maxDurationSeconds (3 minutes for solos, 5 for ensembles)
 * - Storage: Supabase Storage (always overwrites with latest version)
 */
const MusicUpload = ({ 
  registrationId, 
  variationNumber,
  existingMusicUrl,
  existingMusicFileName,
  onUploadComplete,
  onUploadError,
  maxDurationSeconds = 180
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [fileInfo, setFileInfo] = useState(null);

  // Supported audio formats
  const SUPPORTED_FORMATS = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/aac', 'audio/x-m4a', 'audio/m4a', 'audio/flac', 'audio/ogg'];
  const MAX_DURATION_SECONDS = maxDurationSeconds;
  const maxMinutes = `${MAX_DURATION_SECONDS / 60} minutes`;
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

  /**
   * Validate audio duration using Web Audio API
   */
  const validateAudioDuration = (file) => {
    return new Promise((resolve, reject) => {
      const audio = document.createElement('audio');
      const objectUrl = URL.createObjectURL(file);
      
      audio.addEventListener('loadedmetadata', () => {
        URL.revokeObjectURL(objectUrl);
        const duration = audio.duration;
        
        if (duration > MAX_DURATION_SECONDS) {
          reject(new Error(`Audio duration is ${Math.round(duration)}s. Maximum allowed is ${MAX_DURATION_SECONDS}s (${maxMinutes})`));
        } else {
          resolve(duration);
        }
      });
      
      audio.addEventListener('error', () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Unable to read audio file. Please ensure it\'s a valid audio file.'));
      });
      
      audio.src = objectUrl;
    });
  };

  /**
   * Handle file selection
   */
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setSuccess(false);
    setProgress(0);

    try {
      // Validate file type
      if (!SUPPORTED_FORMATS.includes(file.type)) {
        const fileExt = file.name.split('.').pop()?.toLowerCase();
        if (!['mp3', 'wav', 'aac', 'm4a', 'flac', 'ogg'].includes(fileExt)) {
          throw new Error(`Unsupported file format: ${file.type || fileExt}. Please upload: MP3, WAV, AAC, M4A, FLAC, or OGG.`);
        }
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        throw new Error(`File size is ${(file.size / 1024 / 1024).toFixed(1)}MB. Maximum allowed is 50MB.`);
      }

      setUploading(true);
      setProgress(10);

      // Validate audio duration
      console.log('🎵 Validating audio duration...');
      const duration = await validateAudioDuration(file);
      console.log('✅ Audio duration valid:', Math.round(duration), 'seconds');
      setProgress(30);

      // Upload to Supabase Storage
      console.log('📤 Uploading to Supabase Storage...');
      
      // File path: registrations/{registrationId}/variation_{variationNumber}_music
      const fileExt = file.name.split('.').pop();
      const filePath = `registrations/${registrationId}/variation_${variationNumber}_music.${fileExt}`;
      
      // Upload (upsert: true will overwrite if exists)
      const { error: uploadError } = await supabase.storage
        .from('music-uploads')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true // Always overwrite with latest version
        });

      if (uploadError) {
        console.error('❌ Upload error:', uploadError);
        throw new Error(uploadError.message || 'Failed to upload file');
      }

      setProgress(90);

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('music-uploads')
        .getPublicUrl(filePath);

      console.log('✅ Music uploaded successfully:', publicUrl);
      
      setProgress(100);
      setSuccess(true);
      setFileInfo({
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(2),
        duration: Math.round(duration),
        url: publicUrl
      });

      // Callback
      if (onUploadComplete) {
        onUploadComplete({
          url: publicUrl,
          fileName: file.name,
          duration: Math.round(duration)
        });
      }

      // Reset after 3 seconds
      setTimeout(() => {
        setUploading(false);
        setProgress(0);
      }, 3000);

    } catch (err) {
      console.error('❌ Music upload error:', err);
      setError(err.message);
      setUploading(false);
      setProgress(0);
      
      if (onUploadError) {
        onUploadError(err.message);
      }
    }
  };

  return (
    <Paper
      p="md"
      radius="md"
      style={{
        border: '2px dashed #e0e0e0',
        backgroundColor: '#fafafa',
        cursor: uploading ? 'not-allowed' : 'pointer'
      }}
      onClick={() => !uploading && fileInputRef.current?.click()}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".mp3,.wav,.aac,.m4a,.flac,.ogg,audio/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={uploading}
      />

      <Stack spacing="sm">
        {/* Upload Icon/Status */}
        <div style={{ textAlign: 'center' }}>
          {success ? (
            <IconCheck size={48} color="#4caf50" />
          ) : uploading ? (
            <IconUpload size={48} color="#d4a574" />
          ) : (
            <IconMusic size={48} color="#999" />
          )}
        </div>

        {/* Status Text */}
        {success && fileInfo ? (
          <Alert color="green" icon={<IconCheck size="1rem" />}>
            <Text size="sm" weight={600} mb="xs" style={{ textAlign: 'left' }}>
              Upload Complete!
            </Text>
            <Text size="xs" style={{ textAlign: 'left' }}>
              {fileInfo.name} ({fileInfo.size} MB, {fileInfo.duration}s)
            </Text>
          </Alert>
        ) : error ? (
          <Alert color="red" icon={<IconX size="1rem" />}>
            <Text size="sm" weight={600} mb="xs" style={{ textAlign: 'left' }}>
              Upload Failed
            </Text>
            <Text size="xs" style={{ textAlign: 'left' }}>
              {error}
            </Text>
          </Alert>
        ) : (
          <>
            <Text size="sm" weight={600} align="center" color="dimmed">
              {uploading ? 'Uploading Music...' : existingMusicUrl ? 'Upload New Music' : 'Upload Variation Music'}
            </Text>
            <Text size="xs" align="center" color="dimmed">
              {uploading ? 'Please wait...' : 'Click to select or drag and drop'}
            </Text>
            <Text size="xs" align="center" color="dimmed" style={{ fontSize: '0.75rem' }}>
              Supported: MP3, WAV, AAC, M4A, FLAC, OGG • Max duration: {maxMinutes}
            </Text>
          </>
        )}

        {/* Progress Bar */}
        {uploading && (
          <Progress value={progress} size="sm" color="gold" animated />
        )}

        {/* Existing File Info */}
        {existingMusicUrl && !success && !error && !uploading && (
          <Alert color="blue" variant="light">
            <Text size="xs" weight={600} mb={4} style={{ textAlign: 'left' }}>
              ✓ Current file:
            </Text>
            <Text size="xs" style={{ textAlign: 'left', wordBreak: 'break-word' }}>
              {existingMusicFileName || 'Music file uploaded'}
            </Text>
            <Text size="xs" color="dimmed" mt={4} style={{ textAlign: 'left' }}>
              Click to replace with a new file.
            </Text>
          </Alert>
        )}

        {/* Upload Button (Alternative to Click) */}
        {!uploading && !success && (
          <Group justify="center" mt="xs">
            <Button
              size="xs"
              variant="light"
              leftSection={<IconUpload size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              Select File
            </Button>
          </Group>
        )}
      </Stack>
    </Paper>
  );
};

export default MusicUpload;
